import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';

import { PrestadorDadosGeraisService } from '@/core/api/sicom-api/registro/prestador/prestador-dados-gerais.service';
import { PrestadorGeridasService } from '@/core/api/sicom-api/registro/prestador/prestador-geridas/prestador-geridas.service';
import { PrestadorGeridaResponse } from '@/core/api/sicom-api/registro/prestador/prestador-geridas/prestador-geridas.model';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { TabContainer, TabContainerOptionType, TabContent } from '@/shared/components/layout/tab-container/tab-container';
import { PesquisarPessoaCustomComponent } from '@/shared/components/pessoa/pesquisar-pessoa-custom/pesquisar-pessoa-custom';
import { DateUtils } from '@/shared/utils/date-utils';
import { CLASSIFICACAO_ADMINISTRADORA } from '@/features/registro/prestadores/prestadores.constants';
import { PrestadorAberturaNavigationService } from '@/features/registro/prestadores/dashboard-prestadores/components/prestador-abertura-navigation.service';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type GeridaTabelaRow = {
    id: string;
    crm: string;
    nome: string;
    crmDiretorTec: string;
    diretorTec: string;
    dtInicio: string;
    dtFim: string;
    documento: { value: string; color: string; textColor: string };
    source: PrestadorGeridaResponse;
};

@Component({
    selector: 'app-gestao-geridas-prestador',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, ReactiveFormsModule, BotaoAcoes, Button, CardContent, ConfirmDialog, CustomTableComponent, Dialog, InputText, PesquisarPessoaCustomComponent, TabContainer, TabContent],
    providers: [ConfirmationService],
    templateUrl: './gestao-geridas-prestador.html',
    styleUrl: './gestao-geridas-prestador.scss'
})
export class GestaoGeridasPrestador {
    pessoa = input<Pessoa>();

    // Componente renderizado sob o slug "geridas" no catálogo de permissões.
    protected readonly roleGeridasEditar = PERMISSOES.prestadores.geridas.editar;
    protected readonly EInconPos = EInconPos;

    get itensAcoes(): AppMenuItem[] {
        return [acaoMenuItem({ id: 'vincular', label: 'Vincular gerida', icon: 'pi pi-plus', cor: 'novo', disabled: !this.classificacaoAdministradora(), permission: this.roleGeridasEditar, command: () => this.novo() })];
    }

    readonly carregando = signal(false);
    readonly verificandoClassificacao = signal(false);
    readonly classificacaoAdministradora = signal<boolean | null>(null);
    readonly ativas = signal<PrestadorGeridaResponse[]>([]);
    readonly inativas = signal<PrestadorGeridaResponse[]>([]);
    readonly ativasTabela = computed<GeridaTabelaRow[]>(() => this.ativas().map((item) => this.toTabelaRow(item)));
    readonly inativasTabela = computed<GeridaTabelaRow[]>(() => this.inativas().map((item) => this.toTabelaRow(item)));

    selected = 'ATIVAS';
    readonly options: TabContainerOptionType[] = [
        { label: 'Ativas', value: 'ATIVAS' },
        { label: 'Inativas', value: 'INATIVAS' }
    ];

    readonly dialogVisivel = signal(false);
    readonly pesquisaVisivel = signal(false);
    readonly salvando = signal(false);
    readonly alteraTipoGestao = signal(false);
    readonly geridaNaoDefinitiva = signal(false);
    readonly nomeArquivo = signal('');
    readonly registroEmEdicao = signal<PrestadorGeridaResponse | null>(null);

    private readonly fb = inject(FormBuilder);
    readonly form = this.fb.nonNullable.group({
        idGerido: [0, [Validators.required, Validators.min(1)]],
        ufGerido: ['', [Validators.required]],
        nome: ['', [Validators.required]],
        crm: [''],
        dtInicio: ['', [Validators.required]],
        dtFim: [''],
        documento: ['', [Validators.required]]
    });

    readonly colunaOrdem = ['crm', 'nome', 'crmDiretorTec', 'diretorTec', 'dtInicio', 'dtFim', 'documento'];

    readonly metadataAtivas: TableMetadata = {
        headers: this.montarHeaders(),
        title: 'nome',
        actions: {
            abrirPrestador: { label: 'Abrir prestador', icon: 'pi pi-external-link', color: 'primary', command: (item: GeridaTabelaRow) => this.abrirPrestador(item.source) },
            editar: { label: 'Editar', icon: 'pi pi-pencil', color: 'secondary', command: (item: GeridaTabelaRow) => this.editar(item.source), permission: PERMISSOES.prestadores.geridas.editar },
            encerrar: { label: 'Encerrar', icon: 'pi pi-calendar-times', color: 'warn', command: (item: GeridaTabelaRow) => this.confirmarEncerramento(item.source), permission: PERMISSOES.prestadores.geridas.editar },
            documento: { label: 'Documento', icon: 'pi pi-file-pdf', color: 'info', command: (item: GeridaTabelaRow) => this.verDocumento(item.source) },
            remover: { label: 'Remover', icon: 'pi pi-trash', color: 'danger', command: (item: GeridaTabelaRow) => this.confirmarRemocao(item.source), permission: PERMISSOES.prestadores.geridas.editar }
        },
        defaultSortField: 'nome',
        defaultSortOrder: 1
    };

    readonly metadataInativas: TableMetadata = {
        headers: this.montarHeaders(),
        title: 'nome',
        actions: {
            abrirPrestador: { label: 'Abrir prestador', icon: 'pi pi-external-link', color: 'primary', command: (item: GeridaTabelaRow) => this.abrirPrestador(item.source) },
            documento: { label: 'Documento', icon: 'pi pi-file-pdf', color: 'info', command: (item: GeridaTabelaRow) => this.verDocumento(item.source) }
        },
        defaultSortField: 'dtFim',
        defaultSortOrder: -1
    };

    private readonly destroyRef = inject(DestroyRef);
    private readonly service = inject(PrestadorGeridasService);
    private readonly dadosGeraisService = inject(PrestadorDadosGeraisService);
    private readonly confirmation = inject(ConfirmationService);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly aberturaNavigationService = inject(PrestadorAberturaNavigationService);

    constructor() {
        effect(() => {
            const pessoa = this.pessoa();
            if (!pessoa?.id || !pessoa.uf) {
                this.limpar();
                return;
            }
            untracked(() => this.verificarClassificacao(pessoa));
        });
    }

    novo(): void {
        this.registroEmEdicao.set(null);
        this.alteraTipoGestao.set(false);
        this.geridaNaoDefinitiva.set(false);
        this.nomeArquivo.set('');
        this.form.reset({
            idGerido: 0,
            ufGerido: '',
            nome: '',
            crm: '',
            dtInicio: DateUtils.toIsoDate(new Date()),
            dtFim: '',
            documento: ''
        });
        this.form.controls.documento.setValidators([Validators.required]);
        this.form.controls.documento.updateValueAndValidity();
        this.dialogVisivel.set(true);
    }

    editar(registro: PrestadorGeridaResponse): void {
        this.registroEmEdicao.set(registro);
        this.alteraTipoGestao.set(false);
        this.geridaNaoDefinitiva.set(false);
        this.nomeArquivo.set('');
        this.form.reset({
            idGerido: registro.idGerido,
            ufGerido: registro.ufGerido,
            nome: registro.nome,
            crm: registro.crm,
            dtInicio: registro.dtInicio,
            dtFim: registro.dtFim ?? '',
            documento: ''
        });
        // Na edição o documento é opcional (null mantém o anexo atual).
        this.form.controls.documento.clearValidators();
        this.form.controls.documento.updateValueAndValidity();
        this.dialogVisivel.set(true);
    }

    abrirPesquisa(): void {
        this.pesquisaVisivel.set(true);
    }

    selecionarGerida(pessoa: PessoaDomain): void {
        const uf = pessoa.uf ?? '';
        const crmDigitos = (pessoa.crm ?? '').replace(/\D/g, '');
        this.form.patchValue({ idGerido: Number(pessoa.id), ufGerido: uf, nome: pessoa.nome ?? '', crm: pessoa.crm ?? '' });
        this.alteraTipoGestao.set(false);
        this.geridaNaoDefinitiva.set(false);

        this.service
            .pesquisar(uf, crmDigitos || undefined, crmDigitos ? undefined : (pessoa.nome ?? undefined))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((lista) => {
                const match = lista.find((item) => item.idGerida === Number(pessoa.id) && item.uf === uf);
                if (!match) {
                    return;
                }
                this.form.patchValue({ nome: match.nome, crm: match.crm });
                this.alteraTipoGestao.set(match.alteraTipoGestao);
                this.geridaNaoDefinitiva.set(!match.definitiva);
            });
    }

    onArquivoSelecionado(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) {
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const resultado = typeof reader.result === 'string' ? reader.result : '';
            const base64 = resultado.includes(',') ? resultado.split(',')[1] : resultado;
            this.form.patchValue({ documento: base64 ?? '' });
            this.form.controls.documento.markAsTouched();
            this.nomeArquivo.set(file.name);
        };
        reader.readAsDataURL(file);
    }

    salvar(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf || this.form.invalid || this.datasInvalidas() || this.geridaNaoDefinitiva()) {
            this.form.markAllAsTouched();
            return;
        }

        const valor = this.form.getRawValue();
        const registroOriginal = this.registroEmEdicao();
        const requisicao = registroOriginal
            ? this.service.editar({
                  id: Number(pessoa.id),
                  uf: pessoa.uf,
                  idGerido: valor.idGerido,
                  ufGerido: valor.ufGerido,
                  dtInicioOriginal: registroOriginal.dtInicio,
                  dtInicio: valor.dtInicio,
                  dtFim: valor.dtFim || null,
                  documento: valor.documento || null
              })
            : this.service.vincular({
                  id: Number(pessoa.id),
                  uf: pessoa.uf,
                  idGerido: valor.idGerido,
                  ufGerido: valor.ufGerido,
                  dtInicio: valor.dtInicio,
                  documento: valor.documento
              });

        this.salvando.set(true);
        requisicao
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.salvando.set(false))
            )
            .subscribe(() => {
                this.dialogVisivel.set(false);
                this.notificacoes.notificar({ severity: 'success', summary: registroOriginal ? 'Gestão atualizada.' : 'Gerida vinculada.' });
                this.carregar();
            });
    }

    confirmarEncerramento(registro: PrestadorGeridaResponse): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) {
            return;
        }
        const hoje = DateUtils.toIsoDate(new Date());
        this.confirmation.confirm({
            message: `Encerrar a gestão de ${registro.nome} nesta data (${this.formatarData(hoje)})?`,
            accept: () =>
                this.service
                    .editar({
                        id: Number(pessoa.id),
                        uf: pessoa.uf,
                        idGerido: registro.idGerido,
                        ufGerido: registro.ufGerido,
                        dtInicioOriginal: registro.dtInicio,
                        dtInicio: registro.dtInicio,
                        dtFim: hoje,
                        documento: null
                    })
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        this.notificacoes.notificar({ severity: 'success', summary: 'Gestão encerrada.' });
                        this.carregar();
                    })
        });
    }

    confirmarRemocao(registro: PrestadorGeridaResponse): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) {
            return;
        }
        this.confirmation.confirm({
            message: `Remover o vínculo de gestão com ${registro.nome}?`,
            accept: () =>
                this.service
                    .remover({
                        id: Number(pessoa.id),
                        uf: pessoa.uf,
                        idGerido: registro.idGerido,
                        ufGerido: registro.ufGerido,
                        dtInicio: registro.dtInicio
                    })
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        this.notificacoes.notificar({ severity: 'success', summary: 'Vínculo removido.' });
                        this.carregar();
                    })
        });
    }

    abrirPrestador(registro: PrestadorGeridaResponse): void {
        this.aberturaNavigationService.abrirPrestador(
            this.pessoa() as PessoaDomain | undefined,
            { id: registro.idGerido, uf: registro.ufGerido, crm: registro.crm ?? undefined, nome: registro.nome ?? undefined, tipo: 'PRESTADOR' },
            '/prestadores/geridas'
        );
    }

    verDocumento(registro: PrestadorGeridaResponse): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) {
            return;
        }
        if (!registro.possuiDocumento) {
            this.notificacoes.notificar({ severity: 'warn', summary: 'Sem documento comprobatório para esta gestão.' });
            return;
        }
        this.service
            .baixarDocumento(Number(pessoa.id), pessoa.uf, registro.idGerido, registro.ufGerido, registro.dtInicio)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((blob) => {
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 30000);
            });
    }

    datasInvalidas(): boolean {
        const { dtInicio, dtFim } = this.form.getRawValue();
        return !!dtInicio && !!dtFim && dtFim < dtInicio;
    }

    private verificarClassificacao(pessoa: Pessoa): void {
        this.verificandoClassificacao.set(true);
        this.classificacaoAdministradora.set(null);
        this.dadosGeraisService
            .buscar({ id: Number(pessoa.id), uf: pessoa.uf })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.verificandoClassificacao.set(false))
            )
            .subscribe({
                next: (dados) => {
                    const valido = dados?.idClassificacao === CLASSIFICACAO_ADMINISTRADORA;
                    this.classificacaoAdministradora.set(valido);
                    if (valido) {
                        this.carregar();
                    } else {
                        this.ativas.set([]);
                        this.inativas.set([]);
                    }
                },
                error: () => {
                    // Em caso de falha ao verificar a classificação, trata como "não administradora"
                    // para exibir a mensagem de indisponibilidade em vez de deixar o card em branco.
                    this.classificacaoAdministradora.set(false);
                    this.ativas.set([]);
                    this.inativas.set([]);
                }
            });
    }

    private carregar(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) {
            return;
        }
        this.carregando.set(true);
        this.service
            .listar(Number(pessoa.id), pessoa.uf)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregando.set(false))
            )
            .subscribe((response) => {
                this.ativas.set(response?.ativas ?? []);
                this.inativas.set(response?.inativas ?? []);
            });
    }

    private limpar(): void {
        this.ativas.set([]);
        this.inativas.set([]);
        this.classificacaoAdministradora.set(null);
    }

    private toTabelaRow(item: PrestadorGeridaResponse): GeridaTabelaRow {
        return {
            id: `${item.ufGerido}-${item.idGerido}-${item.dtInicio}`,
            crm: item.crm || '-',
            nome: item.nome || '-',
            crmDiretorTec: item.crmDiretorTec || '-',
            diretorTec: item.diretorTec || '-',
            dtInicio: this.formatarData(item.dtInicio),
            dtFim: this.formatarData(item.dtFim),
            documento: item.possuiDocumento ? { value: 'Sim', color: '#10b981', textColor: '#ffffff' } : { value: 'Não', color: '#9ca3af', textColor: '#ffffff' },
            source: item
        };
    }

    private montarHeaders(): TableMetadata['headers'] {
        return {
            crm: { type: 'text', value: 'CRM', default: '-', hidden: false },
            nome: { type: 'text', value: 'Razão Social', default: '-', hidden: false, orderBy: { asc: true } },
            crmDiretorTec: { type: 'text', value: 'CRM Diretor Técnico', default: '-', hidden: false },
            diretorTec: { type: 'text', value: 'Diretor Técnico', default: '-', hidden: false },
            dtInicio: { type: 'text', value: 'Início', default: '-', hidden: false },
            dtFim: { type: 'text', value: 'Fim', default: '-', hidden: false },
            documento: { type: 'tag', value: 'Documento', default: '-', hidden: false }
        };
    }

    private formatarData(data?: string | null): string {
        if (!data) {
            return '-';
        }
        const [ano, mes, dia] = data.split('-');
        return ano && mes && dia ? `${dia}/${mes}/${ano}` : data;
    }
}
